<template>
    <v-container>
        <h3 class="text-left">Armazenamento ocupado por Logs: {{ systemLogSizeMB || '-' }} Megabytes</h3>
        <v-divider />

        <v-card-text>
            <h3>Configuração de agendamento automático de expurgo dos Logs</h3>
            <v-form ref="autoPurgeForm">
                <v-row>
                    <v-col cols="10" md="8" class="mt-4">
                        <m-text-field
                                v-input-filter="[$filters.numeric]"
                                v-model="autoPurgeDays"
                                label="Quantidade de dias de existência do Log para expurgo"
                                placeholder="Informe a quantidade de dias"
                                class="required"
                                outlined
                                maxlength="3"
                                :rules="[$rules.required]"
                        />
                    </v-col>
                </v-row>
            </v-form>
            <v-row class="d-flex justify-end">
                <v-col cols="2">
                    <v-btn
                            class="transition"
                            color="primary"
                            rounded
                            @click="saveAutoPurgeParam"
                    >
                        Salvar
                    </v-btn>
                </v-col>
            </v-row>
        </v-card-text>
        <v-divider />

        <v-card-text>
            <h3>Configuração de expurgo manual dos Logs</h3>
            <v-form ref="manualPurgeForm">
                <v-row>
                    <v-col cols="10" md="8" class="mt-4">
                        <m-text-field
                                v-model="manualPurgeDays"
                                label="Quantidade de dias de existência do Log para expurgo"
                                placeholder="Informe a quantidade de dias"
                                class="required"
                                outlined
                                v-input-filter="[$filters.numeric]"
                                maxlength="3"
                                :rules="[$rules.required]"
                        />
                    </v-col>
                </v-row>
            </v-form>
            <v-row class="d-flex justify-end">
                <v-col cols="2">
                    <v-btn
                            depressed
                            class="transition"
                            color="primary"
                            rounded
                            @click="confirmarExpurgo"
                    >
                        <span v-if="$vuetify.breakpoint.xl">Expurgar manualmente</span>
                        <span v-else>Expurgar</span>
                    </v-btn>
                </v-col>
            </v-row>
        </v-card-text>
        <v-divider />

        <v-card-text>
            <h3>Configuração para recebimento do relatório de expurgo via e-mail</h3>
            <v-form ref="addEmailPurgeForm">
                <v-row>
                    <v-col cols="10" md="8" class="mt-4">
                        <m-text-field
                                v-model="purgeEmail"
                                v-input-filter="[$filters.email]"
                                :rules="[$rules.email, $rules.required]"
                                label="Insira seu e-mail abaixo:"
                                placeholder="Ex: usuario@Rodobens.com.br"
                                outlined
                                maxlength="100"
                        />
                    </v-col>
                </v-row>
            </v-form>
            <v-row>
                <v-col cols="10" md="8">
                    <v-data-table mt="2" :headers="emailHeaders" :items="predefinedEmails" item-key="email" class="elevation-1">
                        <template v-slot:item.action="{ item }" class="text-center">
                            <v-icon small @click="removeEmail(item.id)" class="pl-2" color="black">mdi-delete</v-icon>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
            <v-row class="d-flex justify-end">
                <v-col cols="2">
                    <v-btn
                            class="transition"
                            color="primary"
                            rounded
                            @click="saveNewEmailReceiver"
                    >
                        Salvar
                    </v-btn>
                </v-col>
            </v-row>
        </v-card-text>
        <v-divider />
    </v-container>
</template>

<script>
  import { LogPurgeManualScheduleCommand } from '../../../enums/agendamentos_schedule/commands'
  import { alert, notification } from '@/plugins/helpers'
  import { AlertTypes } from '@/plugins/alerts_types'

  export default {
    name: 'LogPurgeConfig',
    data: () => ({
      autoPurgeDays: null,
      manualPurgeDays: null,
      purgeEmail: '',
      predefinedEmails: [],
      emailHeaders: [
        { text: 'E-mails configurados: ', value: 'email' },
        { text: 'Ações', value: 'action', sortable: false }
      ],
      systemLogSizeMB: 0
    }),
    computed: {
      confirmManualPurgeDayText () {
        return Number(this.manualPurgeDays) === 1 ? 'dia' : 'dias'
      }
    },
    mounted () {
      Promise.all(
        this.getSystemLogSizeMB(),
        this.getPredefinedEmails(),
        this.getIntervalDays()
      )
    },
    methods: {
      async saveAutoPurgeParam () {
        if (!this.autoPurgeDays || this.autoPurgeDays < 0) {
          notification(AlertTypes.ERROR, 'Por favor, informe a quantidade de dias para expurgo automático.')
          return
        }

        try {
          await this.$http.put('/painel/purge/logs/schedule', { purge_interval: this.autoPurgeDays })
          notification(AlertTypes.SUCCESS, 'Agendamento automático de expurgo salvo com sucesso!')
        } catch (error) {
          notification(AlertTypes.ERROR, `Não foi possível salvar o agendamento automático de expurgo, por favor, tente novamente mais tarde! Mensagem retornada: ${error}!`)
        }
      },
      async saveManualPurge () {
        if (!this.manualPurgeDays || this.manualPurgeDays < 0) {
          notification(AlertTypes.ERROR, 'Por favor, informe a quantidade de dias para expurgo manual.')
          return
        }

        const command = LogPurgeManualScheduleCommand(this.manualPurgeDays)

        try {
          await this.$http.post('/painel/agendamentos/agendar_comando', { comando: command })
          notification(AlertTypes.SUCCESS, 'O processo de expurgo de logs está sendo iniciado.')
        } catch (error) {
          notification(AlertTypes.ERROR, 'Erro ao realizar expurgo manual: ' + error.message)
        }
      },
      async removeEmail (id) {
        try {
          await this.$http.delete(`/painel/purge/logs/email/delete/${id}`)
          this.predefinedEmails = this.predefinedEmails.filter((email) => email.id !== id)

          notification(AlertTypes.SUCCESS, 'E-mail excluído com sucesso.')
        } catch (error) {
          if (error.response && error.response.status === 500) {
            notification(AlertTypes.ERROR, 'Falha ao excluir o e-mail. Tente novamente mais tarde.')
          } else {
            notification(AlertTypes.ERROR, 'E-mail não encontrado.')
          }
        }
      },
      async saveNewEmailReceiver () {
        if (this.$refs.addEmailPurgeForm.validate()) {
          const emailExists = this.predefinedEmails.some(item => item.email === this.purgeEmail)
          if (emailExists) {
            notification(AlertTypes.WARN, 'Este e-mail já está salvo na lista.')
            return
          }

          try {
            const { data } = await this.$http.post('/painel/purge/logs/email/save', { email: this.purgeEmail })
            if (data.emailCreated) {
              this.predefinedEmails.push(data.emailCreated)
            }
            this.purgeEmail = ''
            this.$refs.addEmailPurgeForm.resetValidation()
            notification(AlertTypes.SUCCESS, 'E-mail salvo com sucesso!')
          } catch (error) {
            notification(AlertTypes.ERROR, 'Erro ao salvar o e-mail de expurgo de logs. Tente novamente mais tarde.')
          }
        }
      },
      async getPredefinedEmails () {
        try {
          const response = await this.$http.get('/painel/purge/logs/email')
          this.predefinedEmails = response.data.emails
        } catch (error) {
          notification(AlertTypes.ERROR, 'Erro ao buscar e-mails configurados.')
        }
      },
      async getIntervalDays () {
        try {
          const response = await this.$http.get('/painel/purge/logs/interval')
          this.autoPurgeDays = response.data.purge_interval ?? null
        } catch (error) {
          notification(AlertTypes.ERROR, 'Erro ao obter o intervalo de expurgo.')
        }
      },
      async getSystemLogSizeMB () {
        try {
          const response = await this.$http.get('/painel/purge/logs/storage-size')
          this.systemLogSizeMB = response.data.totalSizeMB
        } catch (error) {
          notification(AlertTypes.ERROR, 'Erro ao obter o tamanho do armazenamento dos logs.')
          this.systemLogSizeMB = null
        }
      },
      confirmarExpurgo () {
        if (!this.manualPurgeDays || this.manualPurgeDays < 0) {
          notification(AlertTypes.ERROR, 'Por favor, informe a quantidade de dias para expurgo manual.')
          return
        }

        alert({
          text: `Tem certeza de que deseja excluir os registros de log?\nVocê irá apagar os logs com mais de ${this.manualPurgeDays} ${this.confirmManualPurgeDayText}.\nEssa ação não poderá ser desfeita.`,
          buttons: {
            'Cancelar': () => {},
            'Excluir': () => {
              this.saveManualPurge()
            }
          }
        })
      }
    }
  }
</script>

<style scoped>
</style>
